/**
 * Mapas canonicos de labels e tones para status de Appointment e
 * AppointmentFinancialStatus. PONTO OFICIAL UNICO - qualquer pagina
 * que precisar mostrar label de status DEVE importar daqui.
 *
 * REGRA DE NEGOCIO (zero-hardcoded):
 * Estes mapas sao fallback PT-BR. A fonte oficial sao chaves
 * PlatformMessage no DB ('status.appointment.<KEY>' e
 * 'status.financial.<KEY>'), buscadas via usePlatformLabel/firePlatformToast.
 *
 * Os mapas locais existem so para:
 *  1. SSR-safe (usePlatformLabel e client-side; SSR usa fallback)
 *  2. Bootstrap: antes do primeiro fetch do catalogo
 *  3. Fallback de seguranca: se o operador deletar a chave do DB
 *
 * Quando precisar mostrar label dinamico em componente client, use os
 * hooks `useAppointmentStatusLabel`/`useFinancialStatusLabel` (combinam
 * usePlatformLabel + fallback). Quando precisar do label sincrono
 * (server component ou contexto sem hook), use os mapas diretos.
 */
import {
  usePlatformLabel,
} from './platform-labels';
import type {
  AppointmentStatus,
  AppointmentFinancialStatus,
} from './api';
import type { BadgeTone, BadgeVariant } from '@/components/ui/personalized/feedback/badge/Badge';

// ─── Appointment status (operacional) ────────────────────────────────

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  PENDING: 'Pendente',
  CONFIRMED: 'Confirmado',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  NO_SHOW: 'Não compareceu',
};

export const APPOINTMENT_STATUS_TONES: Record<AppointmentStatus, BadgeTone> = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'danger',
  NO_SHOW: 'neutral',
};

export const APPOINTMENT_STATUS_VARIANTS: Record<AppointmentStatus, BadgeVariant> = {
  PENDING: 'subtle',
  CONFIRMED: 'subtle',
  IN_PROGRESS: 'filled',
  COMPLETED: 'filled',
  CANCELLED: 'filled',
  NO_SHOW: 'subtle',
};

/**
 * Label dinamico via PlatformMessage + fallback PT-BR. Use em
 * client components onde a label precisa refletir customizacao
 * por tenant (admin pode editar em /admin/messages).
 */
export function useAppointmentStatusLabel(status: AppointmentStatus): string {
  return usePlatformLabel(
    `status.appointment.${status}`,
    APPOINTMENT_STATUS_LABELS[status],
  );
}

// ─── Appointment financial status (decisao financeira) ────────────────

export const FINANCIAL_STATUS_LABELS: Record<AppointmentFinancialStatus, string> = {
  UNDECIDED: 'Decisão pendente',
  PAID: 'Pago',
  RECEIVABLE: 'A receber',
  COURTESY: 'Cortesia',
};

// Cor do badge financeiro: fonte única FINANCIAL_STATUS_COLOR (financialBadgeTone
// em lib/financial-status.ts), mesma cor do card/gráfico/legenda. O antigo
// FINANCIAL_STATUS_TONES (mapa hardcoded, Cortesia divergia em azul) foi removido.

/**
 * Label dinamico via PlatformMessage + fallback PT-BR.
 */
export function useFinancialStatusLabel(
  status: AppointmentFinancialStatus,
): string {
  return usePlatformLabel(
    `status.financial.${status}`,
    FINANCIAL_STATUS_LABELS[status],
  );
}
